import axios from 'axios';

const OPENROUTER_API_KEY = import.meta.env.VITE_OPENROUTER_API_KEY;
const OPENROUTER_BASE_URL = 'https://openrouter.ai/api/v1';

class AIService {
  /**
   * Generate weather explanation using AI
   */
  async generateWeatherExplanation(currentWeather, forecast) {
    try {
      const prompt = this.buildWeatherPrompt(currentWeather, forecast);
      const response = await this.callLLM(prompt);
      return response;
    } catch (error) {
      console.error('Error generating weather explanation:', error);
      return this.getFallbackExplanation(currentWeather);
    }
  }

  /**
   * Generate time slot suggestions
   */
  async generateTimeSuggestions(timeSlots, duration) {
    try {
      const prompt = `Analyze these time slots for an outdoor event lasting ${duration} hours and provide brief recommendations:

${timeSlots.map((slot, i) => `
Slot ${i + 1}: ${slot.startTime.toLocaleTimeString()} - ${slot.endTime.toLocaleTimeString()}
- Risk Score: ${slot.riskScore}/100
- Precipitation Probability: ${slot.precipitationProb}%
- Wind Speed: ${slot.windSpeed} m/s
- Temperature: ${slot.temperature}°C
`).join('\n')}

Provide a concise recommendation (2-3 sentences) for the best slot and why.`;

      const response = await this.callLLM(prompt);
      return response;
    } catch (error) {
      console.error('Error generating time suggestions:', error);
      return 'Based on the risk scores, the first time slot appears to offer the most favorable weather conditions for your event.';
    }
  }

  /**
   * Generate notification message
   */
  async generateNotificationMessage(eventName, weatherData, hoursUntil) {
    try {
      const prompt = `Generate a brief weather alert notification for an event:
Event: ${eventName}
Time until event: ${hoursUntil} hours
Weather: ${weatherData.description}, ${weatherData.temp}°C, ${weatherData.precipProb}% chance of rain

Create a short, actionable notification (1-2 sentences).`;

      const response = await this.callLLM(prompt);
      return response;
    } catch (error) {
      console.error('Error generating notification:', error);
      return `Weather alert for ${eventName}: ${weatherData.precipProb}% chance of rain in ${hoursUntil} hours.`;
    }
  }

  /**
   * Analyze route weather risks
   */
  async analyzeRouteRisks(segments) {
    try {
      // Format segment data properly
      const segmentSummary = segments
        .filter(seg => seg.weather && !seg.error)
        .slice(0, 5) // Limit to first 5 segments to keep prompt concise
        .map((seg, i) => {
          return `Segment ${i + 1}:
- Risk Score: ${seg.risk || 0}%
- Distance: ${seg.distance || 0}km
- Weather: ${seg.weather?.description || 'Unknown'}
- Temperature: ${seg.weather?.temp || 0}°C
- Wind: ${seg.weather?.windSpeed || 0}m/s
- Rain Probability: ${seg.weather?.rainProb || 0}%`;
        })
        .join('\n\n');

      const prompt = `Analyze weather risks for this outdoor route:

${segmentSummary}

Provide a brief assessment covering:
1. Overall safety for the route
2. Best time to travel if different from current
3. Specific precautions needed

Keep response under 100 words and be specific about concerning segments.`;

      const response = await this.callLLM(prompt, 200);
      return response;
    } catch (error) {
      console.error('Error analyzing route risks:', error);
      // Provide intelligent fallback based on segments
      const avgRisk = segments.reduce((sum, seg) => sum + (seg.risk || 0), 0) / segments.length;
      if (avgRisk > 60) {
        return 'High weather risk detected on this route. Strong winds or precipitation expected. Consider postponing or choosing an alternative route. Monitor weather updates closely.';
      } else if (avgRisk > 30) {
        return 'Moderate weather risk on this route. Some segments may experience unfavorable conditions. Bring weather protection and monitor forecasts. Be prepared to adjust timing.';
      } else {
        return 'Overall weather conditions are favorable for this route. Clear skies and mild conditions expected. Good visibility and low precipitation risk. Safe to proceed with outdoor activities.';
      }
    }
  }

  /**
   * Call LLM API (OpenRouter or Vertex AI)
   */
  async callLLM(prompt, maxTokens = 300) {
    if (!OPENROUTER_API_KEY) {
      console.warn('No AI API key configured, using fallback responses');
      return this.getFallbackResponse(prompt);
    }

    try {
      console.log('Calling OpenRouter AI with prompt length:', prompt.length);
      
      const response = await axios.post(
        `${OPENROUTER_BASE_URL}/chat/completions`,
        {
          model: 'openai/gpt-3.5-turbo', // Free tier model
          messages: [
            {
              role: 'system',
              content: 'You are a weather expert assistant helping users plan outdoor events. Provide clear, concise, and actionable weather information.',
            },
            {
              role: 'user',
              content: prompt,
            },
          ],
          max_tokens: maxTokens,
          temperature: 0.7,
        },
        {
          headers: {
            'Authorization': `Bearer ${OPENROUTER_API_KEY}`,
            'Content-Type': 'application/json',
            'HTTP-Referer': window.location.origin,
            'X-Title': 'Atmos',
          },
        }
      );

      console.log('OpenRouter AI response received');
      return response.data.choices[0].message.content;
    } catch (error) {
      console.error('Error calling LLM:', error.response?.data || error.message);
      throw error;
    }
  }

  /**
   * Build weather explanation prompt
   */
  buildWeatherPrompt(currentWeather, forecast) {
    const forecastSummary = forecast.list.slice(0, 8).map(item => ({
      time: new Date(item.dt * 1000).toLocaleTimeString(),
      temp: Math.round(item.main.temp),
      pop: Math.round(item.pop * 100),
      description: item.weather[0].description,
    }));

    return `Provide a concise weather summary and outdoor activity recommendation:

Current Conditions:
- Temperature: ${currentWeather.current.temp}°C (feels like ${currentWeather.current.feelsLike}°C)
- Conditions: ${currentWeather.current.description}
- Wind: ${currentWeather.current.windSpeed} m/s
- Humidity: ${currentWeather.current.humidity}%

Next 24 Hours:
${forecastSummary.map(f => `${f.time}: ${f.temp}°C, ${f.pop}% rain chance, ${f.description}`).join('\n')}

Provide:
1. Brief current conditions summary
2. What to expect in the next 24 hours
3. One specific recommendation for outdoor event planning`;
  }

  /**
   * Get fallback explanation when AI is unavailable
   */
  getFallbackExplanation(currentWeather) {
    const temp = currentWeather.current.temp;
    const humidity = currentWeather.current.humidity;
    const wind = currentWeather.current.windSpeed;
    
    let explanation = `Current conditions show ${currentWeather.current.description} with temperatures at ${temp}°C. `;
    
    if (temp > 30) {
      explanation += 'High temperatures may cause discomfort for outdoor activities. ';
    } else if (temp < 10) {
      explanation += 'Cool temperatures, dress warmly for outdoor events. ';
    }
    
    if (wind > 10) {
      explanation += 'Moderate to strong winds may affect outdoor setups. ';
    }
    
    if (humidity > 80) {
      explanation += 'High humidity may make it feel warmer than actual temperature. ';
    }
    
    return explanation + 'Check hourly forecast for detailed planning.';
  }

  /**
   * Get fallback response when AI is unavailable
   */
  getFallbackResponse(prompt) {
    if (prompt.includes('time slots')) {
      return 'Consider choosing the time slot with the lowest risk score and precipitation probability for the best weather conditions.';
    }
    if (prompt.includes('notification')) {
      return 'Weather conditions may affect your event. Check the latest forecast before proceeding.';
    }
    if (prompt.includes('route')) {
      return 'Weather conditions vary along the route. Focus on segments with higher risk scores.';
    }
    return 'Weather analysis complete. Review the data for detailed insights.';
  }
}

export const aiService = new AIService();
